import { useEffect, useMemo, useRef } from "react";
import { useDict } from "@/i18n/useDict";

const HeroSection = () => {
  const { dict } = useDict();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      setTimeout(() => ref.current?.classList.add("visible"), 100);
    }
  }, []);

  const now = new Date();
  const currentDay = now.getDate();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const monthNames = dict.hero.monthNames;

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  // 0 = Sunday, convert to Monday-based (0=Mon..6=Sun)
  const firstDayOfWeek = (new Date(currentYear, currentMonth, 1).getDay() + 6) % 7;

  // Pick 6 random highlight days (excluding today)
  const highlightDays = useMemo(() => {
    const available = Array.from({ length: daysInMonth }, (_, i) => i + 1).filter(d => d !== currentDay);
    const picked: number[] = [];
    for (let i = 0; i < 6 && available.length > 0; i++) {
      const idx = Math.floor(Math.random() * available.length);
      picked.push(available.splice(idx, 1)[0]);
    }
    return picked;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDay, daysInMonth]);

  const days = dict.hero.dayLabels;
  const totalCells = Math.ceil((firstDayOfWeek + daysInMonth) / 7) * 7;
  const cells = Array.from({ length: totalCells }, (_, i) => {
    const day = i - firstDayOfWeek + 1;
    const valid = day >= 1 && day <= daysInMonth;
    const isToday = day === currentDay;
    const hasAppt = highlightDays.includes(day);
    return { day: valid ? day : null, hasAppt, isToday };
  });

  return (
    <section className="pt-32 pb-20 md:pt-40 md:pb-32 px-6">
      <div ref={ref} className="max-w-7xl mx-auto animate-on-scroll flex flex-col lg:flex-row items-center gap-16">
        <div className="flex-1 text-center lg:text-left">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight text-foreground">
            {dict.hero.titleLead}{" "}
            <span className="text-gradient">{dict.hero.titleHighlight}</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
            {dict.hero.subtitle}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <a href="#contact" className="inline-flex items-center justify-center rounded-xl bg-primary text-primary-foreground px-8 py-3.5 text-base font-semibold hover:opacity-90 transition-opacity shadow-lg shadow-primary/25">
              {dict.hero.requestDemo}
            </a>
            <a href="#module" className="inline-flex items-center justify-center rounded-xl border-2 border-primary text-primary px-8 py-3.5 text-base font-semibold hover:bg-secondary transition-colors">
              {dict.hero.discoverModules}
            </a>
          </div>
        </div>

        {/* Mockup */}
        <div className="flex-1 max-w-md w-full">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-foreground">{monthNames[currentMonth]} {currentYear}</span>
              <div className="flex gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-primary/60"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-accent"></span>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {days.map((d, i) => (
                <span key={i} className="text-xs font-medium text-muted-foreground text-center">{d}</span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((c, i) => (
                <div
                  key={i}
                  className={`aspect-square rounded-lg flex items-center justify-center text-xs font-medium transition-colors
                    ${c.day ? "text-foreground" : "text-transparent"}
                    ${c.isToday ? "bg-primary text-primary-foreground" : ""}
                    ${c.hasAppt && !c.isToday ? "bg-accent text-accent-foreground" : ""}
                    ${!c.hasAppt && !c.isToday && c.day ? "hover:bg-muted" : ""}
                  `}
                >
                  {c.day || ""}
                </div>
              ))}
            </div>
            {/* Mini stats */}
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[{ n: "128", l: dict.hero.statToday }, { n: "847", l: dict.hero.statWeek }, { n: "3.2k", l: dict.hero.statMonth }].map((s) => (
                <div key={s.l} className="bg-secondary rounded-lg p-2.5 text-center">
                  <div className="text-base font-bold text-foreground">{s.n}</div>
                  <div className="text-xs text-muted-foreground">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
